import React, { useEffect, useRef } from "react";

export default function ParticleCanvas({ active, stage, heartbeatToken, finaleToken }) {
    const ref = useRef(null);
    const activeRef = useRef(active);
    const stageRef = useRef(stage);
    const pulseRef = useRef({ id: 0, strength: 0 });

    useEffect(() => {
        activeRef.current = active;
    }, [active]);

    useEffect(() => {
        stageRef.current = stage;
    }, [stage]);

    useEffect(() => {
        if (!heartbeatToken) return;
        pulseRef.current = { id: heartbeatToken * 10 + 1, strength: 1 };
    }, [heartbeatToken]);

    useEffect(() => {
        if (!finaleToken) return;
        pulseRef.current = { id: finaleToken * 10 + 2, strength: 2.1 };
    }, [finaleToken]);

    useEffect(() => {
        const canvas = ref.current;
        const context = canvas.getContext("2d");
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let width = 0;
        let height = 0;
        let dpr = 1;
        let raf = 0;
        let particles = [];
        let lastPulseId = 0;
        let flash = 0;

        const makeParticle = () => ({
            x: Math.random() * width,
            y: Math.random() * height,
            r: Math.random() * 1.45 + 0.35,
            vx: (Math.random() - 0.5) * 0.08,
            vy: -Math.random() * 0.17 - 0.025,
            a: Math.random() * 0.42 + 0.1,
            warm: Math.random() > 0.86,
            lastPulse: 0
        });

        const resize = () => {
            width = canvas.clientWidth;
            height = canvas.clientHeight;
            dpr = Math.min(window.devicePixelRatio || 1, 1.65);
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            const mobile = window.matchMedia("(pointer: coarse)").matches;
            const base = reducedMotion ? 24 : mobile ? 46 : 84;
            particles = Array.from({ length: base }, makeParticle);
        };

        const applyPulse = (particle, strength, id) => {
            if (particle.lastPulse === id) return;
            particle.lastPulse = id;
            const cx = width * 0.5;
            const cy = height * 0.47;
            const dx = particle.x - cx;
            const dy = particle.y - cy;
            const length = Math.max(30, Math.hypot(dx, dy));
            particle.vx += (dx / length) * (0.46 + Math.random() * 0.26) * strength;
            particle.vy += (dy / length) * (0.4 + Math.random() * 0.22) * strength;
        };

        const frame = () => {
            context.clearRect(0, 0, width, height);

            if (activeRef.current) {
                const currentPulse = pulseRef.current;
                if (currentPulse.id !== lastPulseId) {
                    lastPulseId = currentPulse.id;
                    flash = currentPulse.strength;
                }

                for (const particle of particles) {
                    if (lastPulseId && particle.lastPulse !== lastPulseId) {
                        applyPulse(particle, pulseRef.current.strength, lastPulseId);
                    }

                    const stageBoost = Math.max(0, stageRef.current - 3) * 0.004;
                    particle.x += particle.vx;
                    particle.y += particle.vy - stageBoost;
                    particle.vx *= 0.989;
                    particle.vy *= 0.989;
                    particle.vy -= 0.0012;

                    if (particle.y < -8 || particle.x < -10 || particle.x > width + 10) {
                        particle.x = Math.random() * width;
                        particle.y = height + Math.random() * 20;
                        particle.vx = (Math.random() - 0.5) * 0.08;
                        particle.vy = -Math.random() * 0.16 - 0.03;
                    }

                    context.beginPath();
                    context.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
                    context.fillStyle = particle.warm
                        ? `rgba(238,100,125,${particle.a})`
                        : `rgba(111,237,222,${particle.a})`;
                    context.fill();

                    if (flash > 0.12 && !reducedMotion) {
                        context.beginPath();
                        context.moveTo(particle.x, particle.y);
                        context.lineTo(particle.x - particle.vx * 9, particle.y - particle.vy * 9);
                        context.strokeStyle = particle.warm
                            ? `rgba(255,155,170,${Math.min(0.42, particle.a * flash)})`
                            : `rgba(157,255,243,${Math.min(0.38, particle.a * flash)})`;
                        context.lineWidth = 0.65;
                        context.stroke();
                    }
                }

                if (flash > 0.01) flash *= 0.94;
            }

            raf = requestAnimationFrame(frame);
        };

        resize();
        window.addEventListener("resize", resize);
        raf = requestAnimationFrame(frame);

        return () => {
            window.removeEventListener("resize", resize);
            cancelAnimationFrame(raf);
        };
    }, []);

    return <canvas ref={ref} className="particle-canvas" aria-hidden="true" />;
}
